import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateRangePickerWithCollapsibleCalendar } from 'ui/datePicker/DateRangePickerWithCollapsibleCalendar';
import { PageHeader } from 'ui/pageHeader';
import { Divider } from 'ui/divider';
import { H4 } from 'ui/theme';
import { Button } from 'ui/button';
import { Checkbox } from 'ui/checkboxV2';
import {
  DATE_FORMAT_FULL_WITH_TIME,
  getNewDate,
  getMonthsAgo,
  formatDateForSave,
  ES_DATE_FORMAT,
} from 'utils/date';
import { FormField } from 'ui/formField';
import { Select } from 'ui/selectv2/Select';
import {
  selectInternalMetricsTableData,
  selectOrganizationMetricsData,
  selectProjectsData,
  selectAdminUsersForFormSelect,
} from '../selectors';

import { fetchInternalMetricsTable, fetchUsers, fetchMetricsByOrganization } from '../actions';
import InternalMetricsSummaryGrid from './internalMetricsSummaryGrid';
import { StyledForm } from './InternalMetrics.styled';
import { OrganizationStatsTable } from './OrganizationStatsTable';
import { ProjectStatsTable } from './ProjectStatsTable';

export const InternalMetrics = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchInternalMetricsTable());
    dispatch(fetchUsers());
  }, [dispatch]);

  const [rangeStart, setRangeStart] = useState(getMonthsAgo(getNewDate(), 1));
  const [rangeEnd, setRangeEnd] = useState(getNewDate());
  const [archivedProjects, setArchivedProjects] = useState(false);
  const [selectOrg, setSelectOrg] = useState({
    label: 'Select an organization',
    value: { orgId: undefined },
  });
  const data = useSelector(selectInternalMetricsTableData);
  const organization = useSelector(selectOrganizationMetricsData);
  const projects = useSelector(selectProjectsData);
  const selectFormattedAdminUsers = useSelector(selectAdminUsersForFormSelect);

  const handleSubmit = (e) => {
    e.preventDefault();
    const timestampStart = formatDateForSave(rangeStart, ES_DATE_FORMAT);
    const timestampEnd = formatDateForSave(rangeEnd, ES_DATE_FORMAT);
    const { orgId } = selectOrg.value;
    dispatch(
      fetchMetricsByOrganization({
        orgId,
        startDateTime: timestampStart,
        endDateTime: timestampEnd,
        archivedProjects,
      }),
    );
  };

  return (
    <>
      <PageHeader
        pageSubSection={
          <p>
            Metrics for all organizations for all time, up-to-date as of right now (
            {getNewDate().format(DATE_FORMAT_FULL_WITH_TIME)})
          </p>
        }
      >
        Internal metrics
      </PageHeader>
      <InternalMetricsSummaryGrid data={data} />
      <Divider />
      <H4>Metrics By Organization over a Date Range</H4>
      <p>
        Use this form to get metrics for a single organization across a specific time range. Once
        the results are queried, they will be displayed in a table below the form.
      </p>
      <StyledForm id="organizationStatsForm" onSubmit={handleSubmit}>
        <FormField labelText="Organization" inputId="baus-organization-select" isRequired>
          <Select
            data-test="baus-organization-dropdown"
            inputId="baus-organization-select"
            onSelect={([value]) => setSelectOrg(value)}
            options={selectFormattedAdminUsers}
            placeholder="Select an organization"
            value={selectOrg}
          />
        </FormField>
        <FormField labelText="Date Range" isRequired>
          <DateRangePickerWithCollapsibleCalendar
            startDate={rangeStart}
            endDate={rangeEnd}
            onDateRangeChange={({ startDate, endDate }) => {
              setRangeStart(startDate);
              setRangeEnd(endDate);
            }}
            blockFutureDates
            isTimeZonePickerDisabled
          />
        </FormField>
        <FormField>
          <Checkbox
            data-test="archived-projects-checkbox"
            label="Include Archived Projects"
            setChecked={(status) => setArchivedProjects(status)}
            status={archivedProjects}
          />
        </FormField>
      </StyledForm>
      <Button type="submit" form="organizationStatsForm" isDisabled={!selectOrg.value.orgId}>
        Get users for timerange
      </Button>
      {!!organization && (
        <>
          <Divider />
          <H4>Organization Stats (Across All Projects)</H4>
          <OrganizationStatsTable organizations={[organization]} />
        </>
      )}
      {projects.length > 0 && (
        <>
          <Divider />
          <H4>Project Stats</H4>
          <ProjectStatsTable projects={projects} />
        </>
      )}
    </>
  );
};
